/**
 * Bandeaux de la coque : un seul à la fois, le plus important d'abord, les
 * autres repliés derrière « +N » (décision du 17/10/2026 ; jusqu'à sept
 * bandeaux s'empilaient au-dessus de la page sur téléphone).
 *
 * Ordre : session d'assistance (Q164), écriture refusée en lecture seule
 * (Q199), abonnement à régulariser (Q198), adresse à confirmer, essai en
 * cours. Chaque bandeau garde ses propres gestes.
 */
import { Fragment, useEffect, useState, type ReactNode } from 'react';
import type { OnboardingTipKey } from '@myequip/shared';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { api, onApiError } from '../../core/api.js';
import { isCompanyOwner, useAuth, useCan } from '../../core/auth.js';
import { t } from '../../core/i18n.js';
import { useSignOut } from '../SignOutGuard.js';
import { Button, ConfirmActionModal } from '../ui.js';
import { Banner } from '../ui/Feedback.js';
import { useResumeRequest } from '../../features/subscription/subscription-hooks.js';

interface ShellBanner {
  id: string;
  /** Plus petit = plus important. */
  priority: number;
  node: ReactNode;
}

// --- Session d'assistance (Q164) -------------------------------------------------------------
function useSupportBanner(): ShellBanner | null {
  const { me } = useAuth();
  // Fin d'assistance : l'écran de déconnexion annonce d'abord les envois en attente.
  const signOut = useSignOut();
  const cache = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const { data, isError } = useQuery({
    queryKey: ['impersonation-status'],
    queryFn: () => api<{ active: boolean }>('/impersonation-status'),
    refetchInterval: 15_000,
    retry: false,
  });
  const end = useMutation({
    mutationFn: () => api('/company/support/end', { method: 'POST', body: { confirmation: 'TERMINER' } }),
    onSuccess: async () => {
      setConfirm(false);
      await cache.invalidateQueries({ queryKey: ['impersonation-status'] });
    },
  });
  if (!data?.active && !me?.support_session) return null;
  const ended = Boolean(me?.support_session && (isError || data?.active === false));
  const label = me?.support_session && !me.support_session.writeEnabled
    ? t('superadmin.banniereImpersonationLecture') : t('superadmin.banniereImpersonation');
  return {
    id: 'assistance',
    priority: 0,
    node: (
      <div className="support-session-banner">
        <Banner tone="warn">
          <span>{ended ? 'Cette assistance est terminée. Revenez à l’administration.' : label}</span>
          {me?.support_session ? (
            <Button variant="secondary" onClick={signOut}>Terminer l’assistance</Button>
          ) : isCompanyOwner(me) ? (
            <Button variant="secondary" onClick={() => { end.reset(); setConfirm(true); }}>
              Terminer l’assistance sur mon entreprise
            </Button>
          ) : null}
        </Banner>
        <ConfirmActionModal
          open={confirm}
          title="Terminer l’assistance sur votre entreprise ?"
          consequence={<p>Les accès temporaires de l’assistance MyEquip seront fermés immédiatement. Les tickets et leur historique restent conservés.</p>}
          confirmLabel="Terminer l’assistance"
          onClose={() => setConfirm(false)}
          onConfirm={() => end.mutate()}
          pending={end.isPending}
          error={end.error?.message}
        />
      </div>
    ),
  };
}

// --- Lecture seule (Q199) : posé dès qu'une écriture est refusée -------------------------------
function useReadOnlyBanner(): ShellBanner | null {
  const [readOnly, setReadOnly] = useState(false);
  useEffect(() => onApiError((code) => {
    if (code === 'read_only_mode') setReadOnly(true);
  }), []);
  if (!readOnly) return null;
  return { id: 'lecture-seule', priority: 1, node: <Banner tone="danger">{t('erreurs.read_only_mode')}</Banner> };
}

// --- Abonnement (Q198–Q199) : annonce AVANT le premier refus ----------------------------------
const ESSAI_ANNONCE: OnboardingTipKey = 'essai_annonce';

/**
 * Demande de reprise (facturation manuelle) : l'équipe MyEquip rétablit l'accès.
 * La demande déjà envoyée se lit sur le serveur : elle n'est jamais reproposée.
 */
function ResumeSubscriptionButton(): ReactNode {
  const resume = useResumeRequest();
  if (resume.loading) return null;
  if (resume.pending) return <strong>{t('abonnement.repriseEnAttente')}</strong>;
  return (
    <button type="button" className="link-button" disabled={resume.sending} onClick={resume.send}>
      {resume.error ? t('abonnement.reprendreReessayer') : t('abonnement.reprendre')}
    </button>
  );
}

function useSubscriptionBanner(): ShellBanner | null {
  const { me, reload } = useAuth();
  const can = useCan();
  const [hidden, setHidden] = useState(false);
  const subscription = me?.subscription;
  if (!subscription || subscription.status === 'actif') return null;
  const { status, daysLeft } = subscription;
  // Le détail de l'abonnement n'est lisible que par qui règle l'entreprise.
  const detail = can('settings.manage') ? (
    <Link to="/entreprise/abonnement">{t('abonnement.voirDetail')}</Link>
  ) : null;
  if (status === 'essai') {
    if (daysLeft === null) return null;
    // Loin de l'échéance, l'essai s'annonce UNE fois ; dans les sept derniers
    // jours, le rappel n'est plus effaçable : la lecture seule ne surprend personne.
    const close = daysLeft <= 7;
    const seen = Boolean((me?.onboarding_state as Record<string, unknown> | undefined)?.[ESSAI_ANNONCE]);
    if (!close && (seen || hidden)) return null;
    const dismiss = async (): Promise<void> => {
      setHidden(true);
      try {
        await api('/auth/onboarding', { method: 'POST', body: { key: ESSAI_ANNONCE } });
        await reload();
      } catch {
        // Hors ligne : masquer localement suffit pour la session.
      }
    };
    const message = close
      ? daysLeft <= 1 ? t('abonnement.essaiDernierJour') : t('abonnement.essaiRestant', { jours: daysLeft })
      : t('abonnement.essaiActif', { jours: daysLeft });
    return {
      id: 'abonnement',
      priority: close ? 2 : 4,
      node: (
        <Banner tone={close && daysLeft <= 1 ? 'warn' : 'info'} {...(close ? {} : { onDismiss: () => void dismiss() })}>
          {message} {detail}
        </Banner>
      ),
    };
  }
  const message = status === 'grace'
    ? t('abonnement.grace')
    : status === 'lecture_seule' ? t('abonnement.lectureSeule') : t('abonnement.suspendu');
  const canResume = isCompanyOwner(me) && !me?.support_session && (status === 'grace' || status === 'lecture_seule');
  return {
    id: 'abonnement',
    priority: 2,
    node: (
      <Banner tone={status === 'grace' ? 'warn' : 'danger'}>
        {message} {canResume ? <ResumeSubscriptionButton /> : null} {detail}
      </Banner>
    ),
  };
}

// --- Adresse non confirmée ---------------------------------------------------------------------
// Sans ce rappel, la confirmation serait invisible — et l'utilisateur découvrirait
// seulement en perdant son mot de passe qu'il ne peut pas récupérer son compte.
function useEmailBanner(): ShellBanner | null {
  const { me } = useAuth();
  const [sent, setSent] = useState(false);
  if (!me || me.email_verified_at || !me.email) return null;
  const resend = async (): Promise<void> => {
    setSent(true);
    try {
      await api('/auth/verify-email/resend', { method: 'POST' });
    } catch {
      // Le message reste le même : l'utilisateur réessaiera plus tard.
    }
  };
  return {
    id: 'adresse',
    priority: 3,
    node: (
      <Banner tone="warn">
        {t('auth.emailAConfirmer', { email: me.email })}{' '}
        {sent ? (
          <strong>{t('auth.emailRenvoye')}</strong>
        ) : (
          <button type="button" className="link-button" onClick={() => void resend()}>
            {t('auth.emailRenvoyer')}
          </button>
        )}
      </Banner>
    ),
  };
}

export function ShellBanners(): ReactNode {
  const [expanded, setExpanded] = useState(false);
  const banners = [useSupportBanner(), useReadOnlyBanner(), useSubscriptionBanner(), useEmailBanner()]
    .filter((banner): banner is ShellBanner => banner !== null)
    .sort((left, right) => left.priority - right.priority);
  if (banners.length === 0) return null;
  const others = banners.length - 1;
  const shown = expanded ? banners : banners.slice(0, 1);
  return (
    <div className="shell-banners">
      {shown.map((banner) => <Fragment key={banner.id}>{banner.node}</Fragment>)}
      {others > 0 ? (
        <button
          type="button"
          className="shell-banners__more"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
          {expanded ? 'Réduire les messages' : `+${others} ${others === 1 ? 'autre message' : 'autres messages'}`}
        </button>
      ) : null}
    </div>
  );
}
